import React, { memo, useState, useEffect } from "react";
import { Menu, MenuItem, Button, Box, Stack, Typography, Avatar, Chip } from "@mui/material";
import { CREATOR_GENDER, FOLLOWERS_ARRAY, getFixedValue, STATIC_LOCATIONS } from "../../utils/constant";
import rightCaret from '../../assets/images/icons/rightCaret.svg';
import { useDispatch, useSelector } from "react-redux";
import { searchInfluencers } from './action';
import { setFilters, clearFilters, setPage } from './ultimateDiscoverSlice';
import { capitalizeWords } from "../../utils/common";
import DeleteIcon from "../../assets/images/icons/X.svg";
import FilterIcon from '../../assets/images/icons/filterIcon.svg';

const EngagementOptions = [
  { label: "1% and above", value: "1" },
  { label: "2% and above", value: "2" },
  { label: "3% and above", value: "3" },
  { label: "5% and above", value: "5" },
  { label: "10% and above", value: "10" }
];

const parseFollowers = (value) => {
    if (!value || value.trim() === '') return { minFollowers: null, maxFollowers: null };
    
    // Check for "250K" or "250K+"
    if (value === "250K" || value === "250K+") {
        return { minFollowers: 250000, maxFollowers: null };
    }
    
    // Parse formats like "10K-25K"
    const parts = value.split('-');
    if (parts.length === 2) {
        const parseNum = (str) => {
            const raw = str.replace('K', '').trim();
            return parseInt(raw) * 1000;
        };
        return { minFollowers: parseNum(parts[0]), maxFollowers: parseNum(parts[1]) };
    }
    return { minFollowers: null, maxFollowers: null };
};

const UltimateNestedDropdown = memo(() => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { filters, totalCount, limit, locationsList, languagesList: languagesListRaw } = useSelector((state) => state.ultimateDiscoverSlice);
  // A failed fetch can leave a non-array in state; never hand that to .map/.find.
  const languagesList = Array.isArray(languagesListRaw) ? languagesListRaw : [];
  const { niches } = useSelector((state) => state.commonSlice);
  const dispatch = useDispatch();

  const handleMenuOpen = (event) => {
    if (anchorEl) return;
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };


  const getActiveChips = () => {
    let active = [];
    if (filters.gender) active.push({ key: 'gender', label: `Gender: ${capitalizeWords(filters.gender)}` });
    if (filters.minFollowers || filters.maxFollowers) {
        let label = `Followers: ${filters.minFollowers ? (filters.minFollowers/1000)+'K' : 0} - ${filters.maxFollowers ? (filters.maxFollowers/1000) + 'K' : 'Mega'}`;
        active.push({ key: 'followers', label: label });
    }
    if (filters.minEngagement) active.push({ key: 'minEngagement', label: `Engagement: ${filters.minEngagement}%+` });
    if (filters.locations && filters.locations.length > 0) {
        if (filters.locations.length === 1 && filters.locations[0] === 'India') {
            active.push({ key: 'location_India', field: 'locations', val: 'India', label: 'Location: India' });
        } else {
            filters.locations.forEach(loc => {
                active.push({ key: `location_${loc}`, field: 'locations', val: loc, label: `Location: ${loc}` });
            });
        }
    }
    if (filters.categories && filters.categories.length > 0) {
        filters.categories.forEach(cat => {
            active.push({ key: `category_${cat}`, field: 'categories', val: cat, label: `Category: ${cat}` });
        });
    }
    if (filters.languages && filters.languages.length > 0) {
        filters.languages.forEach(lang => {
            const langObj = languagesList.find(l => l.abbreviation === lang);
            const label = langObj ? langObj.language : lang;
            active.push({ key: `language_${lang}`, field: 'languages', val: lang, label: `Language: ${label}` });
        });
    }
    return active;
  };

  const menuData = [
    {
      label: "Location",
      children: [],
      key: 'locations'
    },
    {
      label: "Language",
      children: [{ label: "All Languages", value: " ", key: 'languages' }],
      key: 'languages'
    },
    {
      label: "Gender",
      children: [{ label: "Gender (All)", value: ' ', key: 'gender' }],
      key: 'gender',
    },
    {
      label: "Followers",
      children: [{ label: "Followers (All)", value: ' ', key: 'followers' }],
      key: 'followers',
    },
    {
      label: "Category",
      children: [{ label: "Category (All)", value: ' ', key: 'categories' }],
      key: 'categories'
    },
    {
      label: "Engagement",
      children: [{ label: "Engagement (All)", value: ' ', key: 'minEngagement' }],
      key: 'minEngagement'
    }
  ];

  menuData.forEach((item) => {
    if (item.label === 'Location') {
      // IC-valid "City, India" options from the backend cache (kept in sync with
      // influencers.club, India-only). Falls back to the static list until the
      // fetch resolves so the dropdown is never empty.
      const locations = (locationsList && locationsList.length > 0) ? locationsList : STATIC_LOCATIONS;
      const locLabel = locations.map((c) => ({ label: c, value: c, key: 'locations' }));
      item.children = [...item.children, ...locLabel];
    }
    if (item.label === 'Language' && languagesList) {
      const langLabel = languagesList.map((l) => ({ label: l.language, value: l.abbreviation, key: 'languages' }));
      item.children = [...item.children, ...langLabel];
    }
    if (item.label === 'Gender') {
      const genders = CREATOR_GENDER?.map((g) => ({ label: g, value: g.toLowerCase(), key: 'gender' }));
      item.children = [...item.children, ...genders];
    }
    if (item.label === 'Followers') {
      const follower = FOLLOWERS_ARRAY?.map((g) => ({ label: g.label, value: g.value, key: 'followers' }));
      item.children = [...item.children, ...follower];
    }
    if (item.label === 'Category' && niches) {
      const category = niches?.map((g) => ({ label: g, value: g, key: 'categories' }));
      item.children = [...item.children, ...category];
    }
    if (item.label === 'Engagement') {
      const eng = EngagementOptions?.map((g) => ({ label: g.label, value: g.value, key: 'minEngagement' }));
      item.children = [...item.children, ...eng];
    }
  });

  const activeChips = getActiveChips();

  return (
    <div style={{width:'100%',}}>
      <Box sx={{ width: '100%',}}>
        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ alignItems: 'center', width: '100%' }}>
          <Stack direction="row" gap={2} alignItems="center">
            <Chip variant="outlined" onClick={handleMenuOpen} label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, }}><img src={FilterIcon} alt="filtericon" width={20} height={20} />
                <Typography sx={{ fontWeight: 500, fontSize: '16px !important', fontFamily: 'var(--family1)', whiteSpace: 'nowrap', }}>Filter</Typography>
              </Box>
            } sx={{
              "& .MuiTouchRipple-root": { color: "var(--color-blue) !important" },
              height: '34px', width: '108px', background: '#FAFAFA',
              alignItems: 'center', display: 'flex', gap: '4px', borderRadius: '4px', padding: '0px', border: '1px solid #F0F0F0', color: 'black', "&:hover": { backgroundColor: 'transparent !important' }
            }}>
            </Chip>
            
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 1, rowGap: 2, justifyContent: "flex-start"
            }}>
                {activeChips.map((chip) => (
                    <Chip
                    key={chip.key}
                    label={chip.label}
                    variant="outlined"
                    onDelete={() => {
                        if (chip.key === 'followers') {
                            dispatch(setFilters({ minFollowers: null, maxFollowers: null }));
                        } else if (chip.key === 'gender') {
                            dispatch(setFilters({ gender: "" }));
                        } else if (chip.key === 'minEngagement') {
                            dispatch(setFilters({ minEngagement: null, maxEngagement: null }));
                        } else if (chip.field === 'locations') {
                            if (chip.val === 'India') {
                                dispatch(setFilters({ locations: [] }));
                            } else {
                                let currentArr = filters.locations || [];
                                currentArr = currentArr.filter(v => v !== chip.val);
                                dispatch(setFilters({ locations: currentArr }));
                            }
                        } else if (chip.field === 'categories' || chip.field === 'languages') {
                            const currentArr = filters[chip.field] || [];
                            dispatch(setFilters({ [chip.field]: currentArr.filter(v => v !== chip.val) }));
                        }
                        dispatch(setPage(1));
                    }}
                    deleteIcon={<img src={DeleteIcon} alt="delete icon" height={24} width={24} />}
                    sx={{ borderRadius: '5px', border: '1px solid var(--color-blue)', color: 'var(--color-blue)' }}
                    />
                ))}
            </Box>
          </Stack>

          <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
            {totalCount > 0 && <Typography sx={{ color: "#726C6C !important", fontWeight: '600 !important', fontSize: '14px !important', p: 0, m: 0, fontFamily: 'var(--family1)', whiteSpace: 'nowrap' }}>{getFixedValue(totalCount)} Results</Typography>}
            {activeChips.length > 0 && (
              <Button onClick={() => {
                dispatch(clearFilters());
                dispatch(setPage(1));
              }} sx={{ fontWeight: '600 !important', fontSize: '14px !important', p: 0, m: 0, fontFamily: 'var(--family1)', whiteSpace: 'nowrap', color: 'var(--color-blue)' }}>
                Clear Filter
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
      {Boolean(anchorEl) && (<NestedMenu menuItems={menuData} parentAnchorEl={anchorEl} onClose={handleMenuClose} />)}
    </div>
  );
});

export default UltimateNestedDropdown;

const NestedMenu = memo(({ menuItems, parentAnchorEl, onClose, depth = 0, marginMenuLeft = '0' }) => {
  const [openMenus, setOpenMenus] = useState({});
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.ultimateDiscoverSlice);

  const handleMenuClick = (event, index) => {
    setOpenMenus((prev) => ({
      ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: null }), {}),
      [index]: prev[index] ? null : event.currentTarget,
    }));
  };

  const handleItemClick = (event, item, index) => {
    if (item.children) {
      handleMenuClick(event, index);
    } else {
      if (item.key === 'followers') {
        const bounds = parseFollowers(item.value);
        dispatch(setFilters(bounds));
      } else if (item.key === 'minEngagement') {
        dispatch(setFilters({ minEngagement: item.value === ' ' ? null : item.value, maxEngagement: null }));
      } else if (item.key === 'gender') {
        dispatch(setFilters({ gender: item.value === ' ' ? '' : item.value }));
      } else if (item.key === 'locations') {
        if (item.value === ' ' || item.value === 'India') {
            dispatch(setFilters({ locations: ['India'] }));
        } else {
            let current = [...(filters.locations || [])];
            current = current.filter(v => v !== 'India');
            if (!current.includes(item.value)) {
                current.push(item.value);
            }
            dispatch(setFilters({ locations: current }));
        }
      } else if (item.key === 'categories' || item.key === 'languages') {
        if (item.value === ' ') {
            dispatch(setFilters({ [item.key]: [] }));
        } else {
            const current = [...(filters[item.key] || [])];
            if (!current.includes(item.value)) {
                current.push(item.value);
            }
            dispatch(setFilters({ [item.key]: current }));
        }
      }
      
      dispatch(setPage(1));
      onClose();
    }
  };

  return (
    <>
      {<Menu
        disablePortal
        className="custom-filter-discovery"
        anchorEl={parentAnchorEl}
        open={Boolean(parentAnchorEl)}
        onClose={onClose}
        sx={{
          maxHeight: '350px', marginLeft: `${marginMenuLeft}`,
          borderRadius: '12px',
        }}
        anchorOrigin={
          depth === 0
            ? { vertical: "bottom", horizontal: "left" }
            : { vertical: "top", horizontal: "right" }
        }
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {menuItems.map((item, index) => (
          <React.Fragment key={`${depth}-${index}`}>
            <MenuItem
              sx={{
                borderBottom: '1px solid #EEE', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center !important', padding: '10px 0px !important', "&:hover": { background: '#0077FF1A' }, "& .MuiTouchRipple-root": { color: "var(--color-blue) !important" },
              }}
              onClick={(event) => {
                handleItemClick(event, item, index);
              }}
              value={item.value || ""}
            >
              <Box sx={{ flexGrow: 1, px: 2 }}>{item.label}</Box>
              {item.children && <Box sx={{ pr: 2 }}><Avatar src={rightCaret} alt="caret" sx={{ height: '20px', width: '20px' }} /></Box>}
            </MenuItem>
            {item.children && (
              <NestedMenu
                marginMenuLeft="5px"
                menuItems={item.children}
                parentAnchorEl={openMenus[index]}
                onClose={onClose}
                depth={depth + 1}
              />
            )}
          </React.Fragment>
        ))}
      </Menu>}
    </>
  );
});
